/**
 * ALDO mega menu.
 * Takes over the ALDO header menu while this section is on the page:
 *  - desktop, dropdown layout: hover (or click) opens a panel under the header
 *  - desktop, sidebar layout: click opens a side panel styled like the ALDO Modal Menu
 *  - mobile/tablet (below 990px): the menu button opens the side panel, like the live ALDO menu
 */
(function () {
  if (customElements.get('s-aldo-mega-menu')) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const NAV_LINK_SELECTOR = '.s-aldo-header__nav-link.js-open-menu';
  const MENU_BUTTON_SELECTOR = '.js-open-menu-button';
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 180;

  const normalise = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  class AldoMegaMenu extends HTMLElement {
    connectedCallback() {
      this.layout = this.dataset.layout || 'dropdown';
      this.trigger = this.dataset.trigger || 'hover';
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.activePanel = null;
      this.activeLink = null;
      this.openTimer = null;
      this.closeTimer = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerOut = this.onPointerOut.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onScrollOrResize = this.onScrollOrResize.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onEditorBlockSelect = this.onEditorBlockSelect.bind(this);
      this.onEditorDeselect = this.onEditorDeselect.bind(this);
      this.onExternalOpen = this.onExternalOpen.bind(this);
      this.onExternalClose = this.onExternalClose.bind(this);

      // Capture phase so this runs before the ALDO header opens the Modal Menu
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('resize', this.onScrollOrResize, { passive: true });
      window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
      this.desktop.addEventListener('change', this.onMediaChange);

      if (this.layout === 'dropdown') {
        this.panels = Array.from(this.querySelectorAll('.js-amm-panel'));
        this.backdrop = this.querySelector('.js-amm-backdrop');
        document.addEventListener('pointerover', this.onPointerOver);
        document.addEventListener('pointerout', this.onPointerOut);
        document.addEventListener('focusin', this.onFocusIn);
        this.backdrop?.addEventListener('pointerenter', () => this.scheduleClose(0));
        this.backdrop?.addEventListener('click', () => this.closeDropdown());
        this.decorateHeaderLinks();
      }

      // Side menu: always there for mobile, and for desktop with the sidebar layout
      this.sidebar = this.querySelector('.js-amm-sidebar');
      this.modal = this.querySelector('.js-amm-modal');
      this.openers = Array.from(this.querySelectorAll('.js-amm-open-expanded'));
      this.activeOpener = null;
      this.addEventListener('click', (event) => this.onSidebarClick(event));

      // Other parts of the theme (country selector "back") ask the menu to open or close
      if (window.PubSub?.subscribe) {
        this.unsubscribers = [
          window.PubSub.subscribe('open-modal-menu', this.onExternalOpen),
          window.PubSub.subscribe('close-modal-menu', this.onExternalClose),
        ];
      }

      document.addEventListener('shopify:block:select', this.onEditorBlockSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('pointerover', this.onPointerOver);
      document.removeEventListener('pointerout', this.onPointerOut);
      document.removeEventListener('focusin', this.onFocusIn);
      document.removeEventListener('shopify:block:select', this.onEditorBlockSelect);
      document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
      window.removeEventListener('resize', this.onScrollOrResize);
      window.removeEventListener('scroll', this.onScrollOrResize);
      this.desktop.removeEventListener('change', this.onMediaChange);
      (this.unsubscribers || []).forEach((unsubscribe) => typeof unsubscribe === 'function' && unsubscribe());
      this.closeDropdown(true);
      this.closeSidebar();
    }

    isDesktop() {
      return this.desktop.matches;
    }

    // True when the side menu is the menu in use (mobile, or desktop sidebar layout)
    usesSidebar() {
      return !this.isDesktop() || this.layout === 'sidebar';
    }

    onExternalOpen() {
      if (this.usesSidebar()) this.openSidebar(null);
    }

    onExternalClose() {
      this.closeSidebar();
    }

    headerLinkFor(title) {
      const key = normalise(title);
      return Array.from(document.querySelectorAll(NAV_LINK_SELECTOR)).find(
        (link) => normalise(link.dataset.title || link.textContent) === key
      );
    }

    decorateHeaderLinks() {
      this.panels.forEach((panel) => {
        const link = this.headerLinkFor(panel.dataset.title);
        if (!link) return;
        link.setAttribute('aria-controls', panel.id);
        link.setAttribute('aria-expanded', 'false');
      });
    }

    panelFor(title) {
      const key = normalise(title);
      return this.panels?.find((panel) => normalise(panel.dataset.title) === key) || null;
    }

    onMediaChange() {
      this.closeDropdown(true);
      if (this.layout === 'dropdown') this.closeSidebar();
    }

    // ---------- Header link clicks ----------

    onDocumentClick(event) {
      // Mobile menu button: open this side menu instead of the Modal Menu
      const menuButton = event.target.closest(MENU_BUTTON_SELECTOR);
      if (menuButton) {
        if (!this.usesSidebar()) return;
        event.preventDefault();
        event.stopPropagation();
        this.openSidebar(null);
        return;
      }

      const link = event.target.closest(NAV_LINK_SELECTOR);
      if (!link || !this.isDesktop()) return;

      const title = link.dataset.title || link.textContent;

      if (this.layout === 'dropdown') {
        const panel = this.panelFor(title);
        if (!panel) return; // no submenu in this menu: keep the header's normal behaviour

        // Stop the ALDO header from opening the Modal Menu
        event.stopPropagation();

        if (this.trigger === 'click') {
          event.preventDefault();
          if (this.activePanel === panel) {
            this.closeDropdown();
          } else {
            this.openDropdown(panel, link);
          }
        }
        // hover mode: let the click go to the collection page
        return;
      }

      // Sidebar layout
      const opener = this.openers.find((item) => normalise(item.dataset.title) === normalise(title));
      if (!opener) return;
      event.preventDefault();
      event.stopPropagation();
      this.openSidebar(opener);
    }

    // ---------- Dropdown ----------

    onPointerOver(event) {
      if (this.trigger !== 'hover' || !this.isDesktop() || event.pointerType === 'touch') return;

      const link = event.target.closest(NAV_LINK_SELECTOR);
      if (link) {
        const panel = this.panelFor(link.dataset.title || link.textContent);
        clearTimeout(this.closeTimer);
        clearTimeout(this.openTimer);
        if (panel) {
          const delay = this.activePanel ? 0 : OPEN_DELAY;
          this.openTimer = setTimeout(() => this.openDropdown(panel, link), delay);
        } else {
          this.scheduleClose();
        }
        return;
      }

      if (this.activePanel && event.target.closest('.js-amm-panel')) {
        clearTimeout(this.closeTimer);
      }
    }

    onPointerOut(event) {
      if (this.trigger !== 'hover' || !this.activePanel) return;
      const from = event.target.closest(`${NAV_LINK_SELECTOR}, .js-amm-panel`);
      if (!from) return;
      const to = event.relatedTarget;
      if (to && to.closest && to.closest(`${NAV_LINK_SELECTOR}, .js-amm-panel`)) return;
      clearTimeout(this.openTimer);
      this.scheduleClose();
    }

    onFocusIn(event) {
      if (!this.isDesktop()) return;
      const link = event.target.closest(NAV_LINK_SELECTOR);
      if (link && this.activePanel) {
        const panel = this.panelFor(link.dataset.title || link.textContent);
        if (panel && panel !== this.activePanel) this.openDropdown(panel, link);
        return;
      }
      if (this.activePanel && !event.target.closest(`${NAV_LINK_SELECTOR}, .js-amm-panel`)) {
        this.closeDropdown();
      }
    }

    scheduleClose(delay = CLOSE_DELAY) {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeDropdown(), delay);
    }

    updateTop() {
      const shell = document.querySelector('.s-aldo-header__shell') || document.querySelector('.s-aldo-header');
      const bottom = shell ? Math.max(0, shell.getBoundingClientRect().bottom) : 0;
      this.style.setProperty('--amm-top', `${Math.round(bottom)}px`);

      // Line the panel up with the header content (logo on one side, icons on the other)
      const inner = document.querySelector('.s-aldo-header__inner');
      if (inner) {
        const rect = inner.getBoundingClientRect();
        const styles = window.getComputedStyle(inner);
        const left = rect.left + parseFloat(styles.paddingLeft || '0');
        const width = rect.width - parseFloat(styles.paddingLeft || '0') - parseFloat(styles.paddingRight || '0');
        this.style.setProperty('--amm-left', `${Math.round(left)}px`);
        this.style.setProperty('--amm-width', `${Math.round(width)}px`);
      }
    }

    openDropdown(panel, link) {
      if (!panel) return;
      clearTimeout(this.closeTimer);
      this.updateTop();

      if (this.activePanel && this.activePanel !== panel) {
        this.activePanel.classList.remove('is-visible');
        this.activePanel.hidden = true;
      }
      if (this.activeLink && this.activeLink !== link) {
        this.activeLink.classList.remove('is-amm-active');
        this.activeLink.setAttribute('aria-expanded', 'false');
      }

      panel.hidden = false;
      requestAnimationFrame(() => panel.classList.add('is-visible'));
      this.activePanel = panel;

      if (link) {
        link.classList.add('is-amm-active');
        link.setAttribute('aria-expanded', 'true');
        this.activeLink = link;
      }
      this.classList.add('is-open');
    }

    closeDropdown(immediate = false) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (!this.activePanel) return;

      const panel = this.activePanel;
      panel.classList.remove('is-visible');
      if (immediate) {
        panel.hidden = true;
      } else {
        setTimeout(() => {
          if (this.activePanel !== panel) panel.hidden = true;
        }, 200);
      }

      if (this.activeLink) {
        this.activeLink.classList.remove('is-amm-active');
        this.activeLink.setAttribute('aria-expanded', 'false');
      }
      this.activePanel = null;
      this.activeLink = null;
      this.classList.remove('is-open');
    }

    onScrollOrResize() {
      if (this.activePanel) this.updateTop();
    }

    // ---------- Sidebar ----------

    onSidebarClick(event) {
      if (event.target.closest('.js-amm-close')) {
        this.closeSidebar();
        return;
      }

      const searchButton = event.target.closest('.js-amm-open-search');
      if (searchButton) {
        this.closeSidebar();
        window.PubSub?.publish('open-search-modal', { opener: searchButton });
        return;
      }

      const countryButton = event.target.closest('.js-amm-open-country');
      if (countryButton) {
        event.preventDefault();
        this.closeSidebar();
        // Same as the live menu: on small screens "back" in the country selector reopens the menu
        if (window.matchMedia('(min-width: 964px)').matches) {
          window.PubSub?.publish('open-country-selector');
        } else {
          window.PubSub?.publish('open-country-selector', { returnTo: 'modal-menu' });
        }
        return;
      }

      // Store finder link in the footer: close the menu, the store finder opens itself
      if (event.target.closest('[data-store-locator-modal-trigger]')) {
        this.closeSidebar();
        return;
      }

      const opener = event.target.closest('.js-amm-open-expanded');
      if (opener) {
        if (this.activeOpener === opener) {
          this.closeExpanded();
        } else {
          this.openExpanded(opener);
        }
        return;
      }

      if (event.target.closest('.js-amm-back')) {
        this.closeExpanded();
        return;
      }

      // Same as the ALDO Modal Menu: rows with sub-links expand instead of navigating
      const third = event.target.closest('.js-amm-open-third');
      const wrapper = third?.closest('.js-amm-third-wrapper');
      if (wrapper) {
        event.preventDefault();
        wrapper.classList.toggle('is-active');
      }
    }

    openSidebar(opener) {
      if (!this.sidebar) return;
      this.sidebar.classList.add('is-active');
      document.body.classList.add('modal-menu-is-active');
      if (opener) this.openExpanded(opener);
      requestAnimationFrame(() => {
        (this.activeOpener ? this.querySelector('.s-modal-menu__link-title.is-active + .s-modal-menu__menu-expanded .js-amm-back') : this.querySelector('.js-amm-close'))?.focus({ preventScroll: true });
      });
    }

    openExpanded(opener) {
      if (this.activeOpener) this.activeOpener.classList.remove('is-active');
      opener.classList.add('is-active');
      this.modal?.classList.add('has-active-menu');
      this.activeOpener = opener;
    }

    closeExpanded() {
      if (this.activeOpener) this.activeOpener.classList.remove('is-active');
      this.modal?.classList.remove('has-active-menu');
      this.activeOpener = null;
    }

    closeSidebar() {
      if (!this.sidebar || !this.sidebar.classList.contains('is-active')) return;
      this.closeExpanded();
      this.sidebar.classList.remove('is-active');
      this.querySelectorAll('.js-amm-third-wrapper.is-active').forEach((item) => item.classList.remove('is-active'));
      document.body.classList.remove('modal-menu-is-active');
    }

    // ---------- Keyboard ----------

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      if (this.activePanel) {
        const link = this.activeLink;
        this.closeDropdown();
        link?.focus();
      }
      if (this.sidebar?.classList.contains('is-active')) this.closeSidebar();
    }

    // ---------- Theme editor preview ----------

    onEditorBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const title = event.target.closest('[data-title]')?.dataset.title;
      if (this.layout === 'dropdown' && this.isDesktop()) {
        const panel = event.target.closest('.js-amm-panel');
        if (panel) this.openDropdown(panel, this.headerLinkFor(panel.dataset.title));
      } else {
        const expanded = event.target.closest('.s-modal-menu__menu-expanded');
        const opener = expanded?.previousElementSibling || this.openers.find((o) => o.dataset.title === title);
        this.openSidebar(opener && opener.classList.contains('js-amm-open-expanded') ? opener : null);
      }
    }

    onEditorDeselect() {
      this.closeDropdown(true);
      this.closeSidebar();
    }
  }

  customElements.define('s-aldo-mega-menu', AldoMegaMenu);
})();
